/**
 * Redux Store Configuration
 * 
 * Complete Redux store setup with all API slices and middleware
 * Integrates RTK Query APIs: auth, dashboard, drivers, vehicles, roles, branches
 */

import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { setupListeners } from '@reduxjs/toolkit/query';
import rootReducer from './rootReducer';
import { baseApi as api } from '@shared/services/api/baseApi';

/**
 * Configure Redux store with all reducers and middleware
 * 
 * Includes:
 * - All feature slices (auth, booking, role)
 * - RTK Query API (all endpoints via api.reducer)
 * - RTK Query middleware for caching and invalidation
 * - Redux DevTools (development only)
 */
export const store = configureStore({
  reducer: rootReducer,
  
  // Add RTK Query middleware for caching, invalidation, polling, etc.
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      // Configure serializability check
      serializableCheck: {
        // Ignore these action types (useful for redux-persist if added later)
        ignoredActions: [
          'persist/PERSIST',
          'persist/REHYDRATE',
          'persist/PAUSE',
          'persist/PURGE',
          'persist/REGISTER',
        ],
        // Ignore these paths in the state
        ignoredPaths: ['api.queries', 'api.mutations'],
      },
    }).concat(api.middleware),
    
  // Enable Redux DevTools in development only
  devTools: import.meta.env.DEV && {
    name: 'KAPS Admin Dashboard',
    trace: true,
    traceLimit: 25,
  },
});

/**
 * Setup RTK Query listeners
 * 
 * Enables automatic refetching behaviors:
 * - refetchOnFocus: Refetch when window regains focus
 * - refetchOnReconnect: Refetch when network reconnects
 */
setupListeners(store.dispatch);

// ============================================================================
// TypeScript Types
// ============================================================================

/**
 * Root state type - inferred from store
 * 
 * Available state slices:
 * - auth: Authentication state (user, tokens, isAuthenticated)
 * - booking: Booking state (current booking, history)
 * - role: Role state (permissions, roles)
 * - api: RTK Query state (queries, mutations, subscriptions)
 */
export type RootState = ReturnType<typeof store.getState>;

/**
 * App dispatch type - inferred from store
 * Includes all action types from reducers and RTK Query
 */
export type AppDispatch = typeof store.dispatch;

// ============================================================================
// Typed Hooks
// ============================================================================

/**
 * Typed useDispatch hook
 * 
 * Use this instead of plain useDispatch to get proper TypeScript support
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const dispatch = useAppDispatch();
 *   
 *   const handleLogin = () => {
 *     dispatch(setCredentials({ user, accessToken, refreshToken }));
 *   };
 * }
 * ```
 */
export const useAppDispatch = () => useDispatch<AppDispatch>();

/**
 * Typed useSelector hook
 * 
 * Use this instead of plain useSelector to get proper TypeScript support
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const user = useAppSelector(state => state.auth.user);
 *   const isAuthenticated = useAppSelector(selectIsAuthenticated);
 * }
 * ```
 */
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * Store Features:
 * 
 * ✅ RTK Query Integration
 *    - Automatic caching and invalidation
 *    - Normalized data structure
 *    - Optimistic updates support
 *    - Polling and refetching
 * 
 * ✅ Multiple API Slices
 *    - Authentication (login, logout, refresh)
 *    - Dashboard (statistics, aggregations)
 *    - Drivers (CRUD, approval, bulk operations)
 *    - Vehicles (CRUD, file uploads)
 *    - Roles (permissions management)
 *    - Branches (location-based features)
 * 
 * ✅ Feature Slices
 *    - Auth: User state, token management
 *    - Booking: Booking workflow state
 *    - Role: Role and permission management
 * 
 * ✅ TypeScript Support
 *    - Fully typed state
 *    - Typed hooks
 *    - Type inference
 * 
 * ✅ DevTools Integration
 *    - Redux DevTools (development only)
 *    - Action tracing
 *    - Time-travel debugging
 * 
 * ✅ Performance Optimizations
 *    - Automatic memoization
 *    - Selective re-renders
 *    - Request deduplication
 *    - Automatic retries
 */
